import {ApplicationState} from "../../../store";
import {TeamRole} from "../../../store/Models/UserContext";
import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import {SurveyResponseState} from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates";
import {ToxnotComponentProperties} from "../BaseApplicationComponents/ToxnotComponent";
import {ToxnotSignInRequiredComponent} from "../BaseApplicationComponents/ToxnotSignInRequiredComponent";
import {LoadingSpinner, LoadingSpinnerSize} from "../LoadingSpinner";
import {
    ComplianceMaterialSummaryNode,
    ComplianceScoreNodeType,
    RegulationScoresTable
} from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels";
import {connect} from "react-redux";
import {PercentRange} from "../../../utilities/PercentRange";
import {StandardComplianceScoreAsNumber} from "../../../store/Models/SharedMaterials/DataModel";
import {ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter} from "../ToxnotReactModal";
import {QuestionnairePageType} from "../../../store/Models/Surveys/SurveyDataModel";
import {RegulationManager} from "../../../store/Managers/RegulationManager";

import "./styles.css";

// ===============================================================================

interface ComplianceUiMaterialTreeNode {
    dataNode: ComplianceMaterialSummaryNode;
    isExpanded: boolean;
    children: ComplianceUiMaterialTreeNode[];
}

// ===============================================================================

interface OwnProps {
    complianceScoresResult: RegulationScoresTable | null;
    onMaterialNodeSelected?: (node: ComplianceMaterialSummaryNode) => void;
}

type MaterialSummaryProps =
    OwnProps &
    ToxnotComponentProperties &
    typeof SurveyResponseController.actionCreators &
    SurveyResponseState;

interface MaterialSummaryState {
    selectedNode: ComplianceMaterialSummaryNode | null;
    materialsRootNode: ComplianceUiMaterialTreeNode | null;
}

class MaterialSummary extends ToxnotSignInRequiredComponent<MaterialSummaryProps, MaterialSummaryState> {
    // ===============================================================================

    /**
     * A table of rendering helpers; the array is indexed according to the ComplianceScore
     * enum, and when indexed-into returns the Glyphicon and compliance-tag class which
     * represents that score.
     */
    protected static readonly _scoreToMaterialIconAndClassMap = [
        {materialIcon: "help_outline", class: "unknown"},
        {materialIcon: null, class: null},  //{materialIcon: "done", class: "compliant"}, <-- green check mark
        {materialIcon: "flag", class: "noncompliant"}
    ];

    constructor(props: any) {
        super(props);

        this.state = {
            selectedNode: null,
            materialsRootNode: null
        };
    }

    // ===============================================================================

    private rebuildMaterialTree(complianceScoresResult: RegulationScoresTable | null) {
        const setFailedState = () => {
            this.setState({ materialsRootNode: null });
        }

        if (complianceScoresResult === null) {
            setFailedState();
            return;
        }

        const activeQuestionnairePage = this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex].pages[this.props.navContext.surveyQuestionnairePageIndex];

        if (!activeQuestionnairePage.regulationId) {
            setFailedState();
            return;
        }
        
        // SCIP has its own compliance id, but it really is only concerned with REACH SVHC
        const regulationIdToUse = activeQuestionnairePage.type == QuestionnairePageType.ScipQuestionnaire ? RegulationManager.ReachSvhcCandidateRegulationId : activeQuestionnairePage.regulationId;

        const scoreNode = complianceScoresResult[regulationIdToUse]?.materialsRootNode;

        if (!scoreNode) {
            setFailedState();
            return;
        }

        const rootNode = this.buildUiMaterialTree(scoreNode);

        this.setState({ materialsRootNode: rootNode });
    }

    // ===============================================================================

    public componentDidMount() {
        this.rebuildMaterialTree(this.props.complianceScoresResult);
    }

    // ===============================================================================

    public componentDidUpdate(prevProps: Readonly<MaterialSummaryProps>, prevState: Readonly<MaterialSummaryState>, snapshot?: any) {
        if (prevProps.complianceScoresResult !== this.props.complianceScoresResult) {
            this.rebuildMaterialTree(this.props.complianceScoresResult);
        }
    }

    // ===============================================================================

    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    // ===============================================================================

    private buildUiMaterialTree(dataNode: ComplianceMaterialSummaryNode, depth: number = 0) {
        const uiNode: ComplianceUiMaterialTreeNode = {
            dataNode: dataNode,
            isExpanded: depth === 0,
            children: []
        }

        for (let childDataNode of dataNode.children) {
            uiNode.children.push(this.buildUiMaterialTree(childDataNode, depth + 1));
        }

        return uiNode;
    }

    // ===============================================================================

    private openRegulatedListsModal(node: ComplianceMaterialSummaryNode) {
        this.setState({ selectedNode: node });

        $('#substance-lists-modal').modal('show');
    }

    // ===============================================================================

    private selectBomPage(invIndex: number) {
        this.props.setSurveyBomPage(invIndex);
    }

    // ===============================================================================

    /**
     * Returns an anchor element to link to the compliance summary for a material in
     * the material results table
     * @param materialNode The material to be linked
     */
    private getAnchorForMaterialNode(materialNode: ComplianceMaterialSummaryNode): JSX.Element {
        const nameToUse = materialNode.name.length > 0
            ? materialNode.name
            : this.localizeString("Unnamed Material");

        let ret: JSX.Element;
        
        if (this.props.onMaterialNodeSelected) {
            ret = <a role="button" onClick={e => this.props.onMaterialNodeSelected && this.props.onMaterialNodeSelected(materialNode)}>{nameToUse}</a>;
        }
        else {
            ret = <span>{nameToUse}</span>
        }
        
        return ret;
    }

    // ===============================================================================

    /**
     * Returns the anchor element for a substance result, if the substance is a Toxnot
     * substance. If not, the element is html-encoded plain text.
     * @param substanceNode The substance to be linked in the table
     */
    private getAnchorForSubstanceNode(substanceNode: ComplianceMaterialSummaryNode): JSX.Element {
        const substanceText = substanceNode.name + ' [' + substanceNode.casNumber + ']';

        if (substanceNode.substanceId === null) {
            return <>{substanceText}</>;
        } else {
            return (
                <a href={`/Substances/SubstanceDetail/${encodeURIComponent(substanceNode.substanceId)}/${encodeURIComponent(substanceNode.casNumber)}`}
                   target={"_blank"}>
                    {substanceText}
                </a>
            );
        }
    }

    // ===============================================================================

    /**
     * Returns the html for a compliance score span, which consists of the score icon,
     * and the description of the score.
     *
     * @param node The score to be displayed
     */
    private getElementForStandardComplianceScore(node: ComplianceMaterialSummaryNode): JSX.Element {
        const renderingValues = MaterialSummary._scoreToMaterialIconAndClassMap[node.standardScore];

        if (node.standardScore !== StandardComplianceScoreAsNumber.NonCompliant) {
            if (renderingValues.class !== null && renderingValues.materialIcon !== null) {
                return (
                    <span className={"compliance-tag " + renderingValues.class}>
                        <i className="material-icons">{renderingValues.materialIcon}</i>
                    </span>
                );
            }
            else {
                return (
                    <span className={"compliance-tag"}>
                        <i className="material-icons"></i>
                    </span>
                );
            }
        }
        else {
            return (
                <span className={"compliance-tag " + renderingValues.class} onClick={() => this.openRegulatedListsModal(node)}>
                    <i className="material-icons">{renderingValues.materialIcon}</i>
                </span>
            );
        }
    }

    // ===============================================================================

    private getTrForMaterialSummaryNodeRow(uiNode: ComplianceUiMaterialTreeNode, depth: number, rowIndex: number): JSX.Element {
        const pixelsOffsetPerLevel = 40;

        const dataNode = uiNode.dataNode;

        const nameToUse = dataNode.name.length > 0
            ? dataNode.name
            : this.localizeString('Unnamed Material');

        const anchor = dataNode.type === ComplianceScoreNodeType.material
            ? this.getAnchorForMaterialNode(dataNode)
            : this.getAnchorForSubstanceNode(dataNode);

        const glyphSpan = this.getElementForStandardComplianceScore(dataNode);

        const expandChildrenButton: JSX.Element | null = uiNode.children.length > 0
            ? <button type="button" className="btn btn-sm btn-default toggle-expand-button" onClick={e => this.expandMaterialButton_click(uiNode)}><span className={`glyphicon ${(uiNode.isExpanded ? "glyphicon-menu-down" : "glyphicon-menu-right")}`}></span></button>
            : null;

        // let noteHtml = "";
        //
        // if (!dataNode.isRegulationApplicable) {
        //     noteHtml = (
        //         '&nbsp;' + '<span class="materials-summary-row-label not-applicable">' + htmlEncode(basePageViewController.localizeString("Regulation not applicable")) + '</span>&nbsp;'
        //     );
        // }
        //
        // if (dataNode.isDeterminedBySummaryData) {
        //     if (!dataNode.isRegulationApplicable) {
        //         noteHtml = (
        //             '&nbsp;' + '<span class="materials-summary-row-label">' + htmlEncode(basePageViewController.localizeString("Material has a Compliance Response; Regulation not applicable")) + '</span>&nbsp;'
        //         );
        //     } else {
        //         noteHtml = (
        //             '&nbsp;' + '<span class="materials-summary-row-label">' + htmlEncode(basePageViewController.localizeString("Material has a Compliance Response")) + '</span>&nbsp;'
        //         );
        //     }
        // }

        // Put it all together

        const cultureName = this.props.appServices.userContext!.cultureName;

        const productPercentByWeightSerialized = dataNode.hasProductPercentByWeight
            ? PercentRange.fromSerializedRange(dataNode.productPercentByWeight, this.props.appServices.userContext!.cultureName).toString(cultureName, 2)
            : "";

        const offset = (depth * pixelsOffsetPerLevel).toString() + 'px';

        //$ret.find('.material-icons').attr('title', this.getActiveComplianceRenderer().getTooltipDescriptionForNode(uiNode.dataNode));

        return (
            <tr key={rowIndex}>
                <td style={{paddingLeft: offset}}>
                    {glyphSpan}
                    {anchor}
                    {" "}
                    {expandChildrenButton}
                </td>
                <td className="text-center">
                    {productPercentByWeightSerialized}
                </td>
            </tr>
        );
    }

    // ===============================================================================

    private appendNodeRowToTableRows(node: ComplianceUiMaterialTreeNode, rows: JSX.Element[], depth: number) {
        const thisNodeTr = this.getTrForMaterialSummaryNodeRow(node, depth, rows.length);

        rows.push(thisNodeTr);

        if (node.isExpanded) {
            for (let childNode of node.children) {
                this.appendNodeRowToTableRows(childNode, rows, depth + 1);
            }
        }
    }

    // ===============================================================================

    private generateTableRows(): JSX.Element[] {
        if (this.state.materialsRootNode === null) {
            return [];
        }

        const rows: JSX.Element[] = [];

        for (let node of this.state.materialsRootNode.children) {
            this.appendNodeRowToTableRows(node, rows, 0);
        }

        return rows;
    }

    // ===============================================================================

    protected renderConcreteComponent() {
        const scoresResult = this.props.complianceScoresResult;

        if (scoresResult === null) {
            return <LoadingSpinner size={LoadingSpinnerSize.Medium}/>;
        }

        const tableRows = this.generateTableRows();

        return (
            <div className="materials-summary-container col-xs-12 col-md-6">
                <div className="title-container">
                    <h4>{this.localizeString("Materials Summary")}</h4>
                    <h5 className="p-1">
                        <a onClick={() => this.selectBomPage(this.props.navContext.inventoryNavIndex)}>{this.localizeString("Update Ingredient Disclosure")}</a>
                    </h5>
                </div>
                <div className="overflow-table-container">
                    <table className="table tn-table">
                        <thead>
                            <tr>
                                <td className="col-md-3">{this.localizeString("Material")}</td>
                                <td className="col-md-1 text-center">{this.localizeString("Percent by Weight")}</td>
                            </tr>
                        </thead>
                        <tbody>
                            {tableRows}
                        </tbody>
                    </table>
                </div>
                <ToxnotReactModal modalId={"substance-lists-modal"}
                    localizedTitle={`${this.props.selectedInventory?.hasBomPage && this.localizeString(this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex].pages[this.props.navContext.surveyQuestionnairePageIndex].title)} ${this.localizeString("List Data")}`}>
                    <ToxnotReactModalBody>
                        <table className="table regulatory-metadata-table">
                            <thead>
                                <tr>
                                    <td>{this.localizeString("Chemical Registry Number")}</td>
                                    <td>{this.localizeString("List Name")}</td>
                                    <td>{this.localizeString("Reason for Inclusion")}</td>
                                </tr>
                            </thead>
                            <tbody className="overflow-table-container">
                                {this.renderRowLists()}
                            </tbody>
                        </table>
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter localizer={this.props.appServices}
                                            customCloseButtonName={this.localizeString("Done")}/>
                </ToxnotReactModal>
            </div>
        );
    }

    private expandMaterialButton_click = (node:ComplianceUiMaterialTreeNode) => {
        if (this.state.materialsRootNode === null) {
            return;
        }

        const newRootNode = { ...this.state.materialsRootNode };
        node.isExpanded = !node.isExpanded;

        this.setState({
            materialsRootNode: newRootNode
        });
    }

    // ===============================================================================

    private renderRowLists() {
        if ((this.state.selectedNode === null) || !this.props.selectedInventory) {
            return null;

        }

        const nodeRowIndex = this.state.selectedNode.nodeRowIndex;

        const inventoryRow = this.props.selectedInventory.inventoryRows[nodeRowIndex];

        const flaggedListIds = this.state.selectedNode.flaggedListIds;

        const filteredListEntries = inventoryRow.allListMetadataList.filter(ld => flaggedListIds.includes(ld.listId));

        return filteredListEntries.map((listEntry, index) =>
            <tr key={index}>
                <td>{!listEntry.casNumber ? inventoryRow.casNumber : listEntry.casNumber}</td>
                <td>{listEntry.listName}</td>
                <td>{listEntry.reasonForInclusion}</td>
            </tr>
        );
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState, ownProps: OwnProps) => ({
        ...state.surveyResponseState,
        appServices: state.appServices,
        ...ownProps
    }),
    SurveyResponseController.actionCreators
)(MaterialSummary as any);
